import React from "react";
import { motion } from "framer-motion";

function Module2() {
  return (
    <>
      <div className="bg-particles" />
      <div className="page-container">
        <motion.div
          className="module-header"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p className="module-number">Módulo 02</p>
          <h1 className="module-title">AgentCore Runtime</h1>
          <p className="module-description">
            Despliega agentes LangGraph en Amazon Bedrock AgentCore Runtime con aislamiento
            completo de sesion, escalamiento automático y soporte multi-framework.
          </p>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2>🚀 ¿Qué es AgentCore Runtime?</h2>
          <p>
            AgentCore Runtime es la plataforma serverless de AWS para ejecutar agentes de IA
            en produccion. Proporciona aislamiento de sesion, escalamiento automático,
            y soporte nativo para frameworks como LangGraph, Strands, CrewAI y código custom.
          </p>
          <div className="architecture-diagram">
            <div className="flow">
              <div className="node">Tu Codigo LangGraph</div>
              <div className="arrow">→</div>
              <div className="node">AgentCore CLI</div>
              <div className="arrow">→</div>
              <div className="node">AgentCore Runtime</div>
              <div className="arrow">→</div>
              <div className="node">Usuarios</div>
            </div>
          </div>
          <ul>
            <li><strong>Aislamiento de Sesion:</strong> Cada invocacion del agente corre en un entorno aislado</li>
            <li><strong>Escalamiento Automatico:</strong> Escala de 0 a miles de invocaciones sin configuración</li>
            <li><strong>Multi-Framework:</strong> Soporta LangGraph, Strands, CrewAI, AutoGen, y custom code</li>
            <li><strong>Protocolo AG-UI:</strong> Streaming nativo via Server-Sent Events (SSE)</li>
            <li><strong>Direct Code Deploy:</strong> Sube tu código directamente sin contenedores</li>
          </ul>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <h2>📦 Opción 1: Agente con Amazon Bedrock (Claude)</h2>
          <p>
            El agente Comfamito usando LangGraph con Amazon Bedrock como proveedor de LLM.
            Esta es la opcion nativa de AWS sin necesidad de claves externas.
          </p>
          <div className="code-block">
{`# comfamito_bedrock.py - Agente con Amazon Bedrock
from langgraph.graph import StateGraph, MessagesState, START, END
from langchain_aws import ChatBedrock

llm = ChatBedrock(
    model_id="anthropic.claude-sonnet-4-20250514",
    region_name="us-east-1",
)

SYSTEM_PROMPT = """Eres Comfamito, el asistente virtual de Comfama.
Ayudas a los afiliados con información sobre:
- Beneficios y subsidios
- Programas educativos
- Eventos y recreacion
- Tienda Comfama
Siempre responde de forma amable y en espanol."""

def agent_node(state: MessagesState):
    messages = [{"role": "system", "content": SYSTEM_PROMPT}] + state["messages"]
    response = llm.invoke(messages)
    return {"messages": [response]}

graph = StateGraph(MessagesState)
graph.add_node("agent", agent_node)
graph.add_edge(START, "agent")
graph.add_edge("agent", END)
comfamito = graph.compile()`}
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h2>📦 Opción 2: Agente con Azure OpenAI</h2>
          <p>
            El mismo agente Comfamito pero usando Azure OpenAI como proveedor de LLM.
            Util para organizaciones con contratos existentes en Azure.
          </p>
          <div className="code-block">
{`# comfamito_azure.py - Agente con Azure OpenAI
from langgraph.graph import StateGraph, MessagesState, START, END
from langchain_openai import AzureChatOpenAI
import os

llm = AzureChatOpenAI(
    azure_deployment="gpt-4o",
    azure_endpoint=os.environ["AZURE_OPENAI_ENDPOINT"],
    api_key=os.environ["AZURE_OPENAI_API_KEY"],
    api_version="2024-08-01-preview",
)

# ... mismo graph que la opcion Bedrock`}
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h2>⚡ Despliegue con AgentCore CLI</h2>
          <p>
            Usa el CLI de AgentCore para desplegar tu agente directamente desde tu maquina local.
            El proceso de Direct-Code-Deploy empaqueta tu código y lo sube a la nube de AWS.
          </p>
          <div className="step-card">
            <h4>Paso 1: Instalar AgentCore CLI</h4>
            <p>npm install -g @aws/agentcore</p>
          </div>
          <div className="step-card">
            <h4>Paso 2: Inicializar el proyecto</h4>
            <p>agentcore create --name Comfamito --defaults</p>
          </div>
          <div className="step-card">
            <h4>Paso 3: Probar localmente</h4>
            <p>agentcore dev</p>
          </div>
          <div className="step-card">
            <h4>Paso 4: Desplegar a AWS</h4>
            <p>agentcore deploy</p>
          </div>
          <div className="step-card">
            <h4>Paso 5: Invocar el agente</h4>
            <p>agentcore invoke --name Comfamito --prompt "Hola, que beneficios tengo?"</p>
          </div>
        </motion.div>

        <motion.div
          className="content-section"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <h2>🏗️ Soporte con Terraform</h2>
          <p>
            AgentCore Runtime tambien se puede gestionar con Terraform para equipos que ya usan
            Infrastructure as Code. El provider de AWS permite crear y configurar agentes
            de forma declarativa, integrando con pipelines de CI/CD existentes.
          </p>
          <div className="highlight-box">
            <h4>Beneficios del enfoque IaC</h4>
            <p>
              Versionamiento, reproducibilidad, auditoria, y despliegues automatizados.
              Ideal para entornos empresariales como Comfama con multiples ambientes
              (dev, staging, produccion).
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <div className="source-links-banner">
            <h4>📖 Documentacion Oficial AWS</h4>
            <div className="source-links-list">
              <div className="source-link-item">
                <span>AgentCore →</span>
                <a href="https://docs.aws.amazon.com/bedrock/latest/userguide/agentcore.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/bedrock/latest/userguide/agentcore.html
                </a>
              </div>
              <div className="source-link-item">
                <span>Bedrock Agents →</span>
                <a href="https://docs.aws.amazon.com/bedrock/latest/userguide/agents.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/bedrock/latest/userguide/agents.html
                </a>
              </div>
              <div className="source-link-item">
                <span>LangGraph →</span>
                <a href="https://langchain-ai.github.io/langgraph/" target="_blank" rel="noopener noreferrer">
                  langchain-ai.github.io/langgraph
                </a>
              </div>
              <div className="source-link-item">
                <span>Bedrock Models →</span>
                <a href="https://docs.aws.amazon.com/bedrock/latest/userguide/models-supported.html" target="_blank" rel="noopener noreferrer">
                  docs.aws.amazon.com/bedrock/latest/userguide/models-supported.html
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
}

export default Module2;
